import React, { useState, useMemo, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import { Tag, Plus, CheckIcon, ChevronLeft } from "lucide-react";
import { LabelInterface } from "@/types/labels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { useIssuesStore } from "@/store/issues-store";
import { toast } from "sonner";
import { COLOR_LIST } from "@/utils/constants";
import { cn } from "@/lib/utils";
import { Issue } from "@/types/issues";
import { useSupabaseStore } from "@/store/zustand/supabaseStore";

interface LabelBadgeProps {
  className?: string;
  issue?: Issue;
  projectLabels?: LabelInterface[];
  labelId?: string;
  labelsToStack?: LabelInterface[];
  labelIdsFallback?: string[] | null;
  labelIdsToStackFallback?: string[] | null;
  onChange?: (newLabels: string[]) => void;
  iconColor?: string;
}

export const LabelBadge: React.FC<LabelBadgeProps> = ({
  className = "",
  issue,
  projectLabels = [],
  labelId = "",
  labelsToStack = [],
  labelIdsFallback = null,
  labelIdsToStackFallback = null,
  onChange,
  iconColor = "var(--text-primary)",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [newLabelName, setNewLabelName] = useState("");
  const [labelView, setLabelView] = useState<"list" | "new" | "colorSelect">(
    "list"
  );
  const { updateIssueLabels, createIssueLabel } = useIssuesStore();
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const labelInputRef = React.useRef<HTMLInputElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  if (!projectLabels) {
    projectLabels = [];
  }
  const label = projectLabels.find((l) => l.id === labelId);

  const currentLabels = (issue?.labels ?? labelIdsFallback ?? []).filter(
    (l) => l !== null
  ) as unknown as string[];

  let currentLabelsToStack: LabelInterface[] = labelsToStack.filter(
    (l) => l !== null
  );
  if (labelIdsToStackFallback) {
    currentLabelsToStack = [
      ...currentLabelsToStack,
      ...labelIdsToStackFallback.map(
        (l) => projectLabels.find((pl) => pl.id === l) as LabelInterface
      ),
    ];
  }

  const supabaseClient = useSupabaseStore((state) => state.supabaseClient);

  // Add focus effect when dropdown opens
  useEffect(() => {
    if (isOpen) {
      // Use a small timeout to ensure the dropdown is rendered
      const timeoutId = setTimeout(() => {
        if (labelView === "list") {
          searchInputRef.current?.focus();
        } else if (labelView === "new") {
          labelInputRef.current?.focus();
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, labelView]);

  // Add scrollbar styles
  const scrollbarStyles = `
    .context-menu-scroll::-webkit-scrollbar {
      width: 8px;
    }
    .context-menu-scroll::-webkit-scrollbar-track {
      background: transparent;
    }
    .context-menu-scroll::-webkit-scrollbar-thumb {
      background-color: rgba(255, 255, 255, 0.3);
      border-radius: 4px;
    }
    .context-menu-scroll::-webkit-scrollbar-thumb:hover {
      background-color: rgba(255, 255, 255, 0.5);
    }
  `;

  const sortedAndFilteredLabels = useMemo(() => {
    const filtered = projectLabels.filter((label) =>
      label.label?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Split into applied and unapplied labels
    const applied = filtered
      .filter((label) => currentLabels.some((l) => l === label.id))
      .sort((a, b) =>
        a.label?.toLowerCase().localeCompare(b.label?.toLowerCase() ?? "")
      );

    const available = filtered
      .filter((label) => !currentLabels.some((l) => l === label.id))
      .sort((a, b) =>
        a.label?.toLowerCase().localeCompare(b.label?.toLowerCase() ?? "")
      );

    return { applied, available };
  }, [projectLabels, searchQuery, currentLabels]);

  const handleLabelToggle = async (labelId: string) => {
    const selectedLabel = projectLabels.find((l) => l.id === labelId);
    if (!selectedLabel) return;

    try {
      const hasLabel = currentLabels.some((l) => l === labelId);
      if (hasLabel) {
        const updatedLabelIds = currentLabels.filter((l) => l !== labelId);
        const updatedLabelInterfaces = updatedLabelIds.map((l) =>
          projectLabels.find((pl) => pl.id === l)
        );

        if (!issue && onChange) {
          onChange(updatedLabelIds);
          return;
        }

        await updateIssueLabels(
          issue?.id ?? "",
          (updatedLabelInterfaces ?? []) as LabelInterface[]
        );
        toast.success(`Removed label: ${selectedLabel.label}`);
      } else {
        const updatedLabelIds = [...currentLabels, labelId];
        const updatedLabelInterfaces = updatedLabelIds.map((l) =>
          projectLabels.find((pl) => pl.id === l)
        );

        if (!issue && onChange) {
          onChange(updatedLabelIds);
          return;
        }

        await updateIssueLabels(
          issue?.id ?? "",
          (updatedLabelInterfaces ?? []) as LabelInterface[]
        );
        toast.success(`Added label: ${selectedLabel.label}`);
      }
    } catch (error) {
      console.error("Failed to update label:", error);
      toast.error("Failed to update label");
    }
  };

  const handleColorSelect = async (color: any) => {
    // Create a new label with the selected color
    const newLabel: LabelInterface = {
      id: crypto.randomUUID(),
      name: newLabelName,
      color: color.value,
      label: newLabelName, // For backward compatibility
    };

    // Add the label to the issue
    createIssueLabel(newLabel);
    toast.success(`Created new label: ${newLabelName}`);

    // fetch the updated project
    if (!supabaseClient) return;
    // wait a second
    // retry 3 times with a delay of 1 second
    for (let i = 0; i < 3; i++) {
      setTimeout(() => {
        supabaseClient
          .from("projects")
          .select("*")
          .eq("id", issue?.projectId ?? "")
          .single()
          .then(({ data, error }) => {
            if (error) {
            } else {
              const labels = data?.labels as any[];
              const label = labels?.find((l: any) => l.id === newLabel.id);
              const currentLabelInterfaces = currentLabels.map((l) =>
                projectLabels.find((pl) => pl.id === l)
              );
              if (label) {
                updateIssueLabels(issue?.id ?? "", [
                  ...currentLabelInterfaces,
                  label,
                ]);
                // close the dropdown
                setIsOpen(false);
              } else {
                console.error("Label not found");
              }
            }
          });
      }, 1000);
    }
  };

  const BadgeContent = ({
    showName = true,
    badgeLabel = null,
  }: {
    showName?: boolean;
    badgeLabel?: LabelInterface | null;
  }) => (
    <>
      {badgeLabel ? (
        <>
          <div
            className="size-2.5 rounded-[3px] flex-shrink-0"
            style={{
              backgroundColor:
                projectLabels.find((l) => l.id === badgeLabel.id)?.color
                  ?.value || "red",
            }}
          />
          {showName && (badgeLabel.label || badgeLabel.name)}
        </>
      ) : (
        <>
          <Tag className="size-4 text-muted-foreground" color={iconColor} />
          Labels
        </>
      )}
    </>
  );

  const StackedBadgeContent = () => (
    <>
      {currentLabelsToStack.slice(0, 3).map((stackingLabel) => (
        <BadgeContent
          key={stackingLabel.id}
          showName={false}
          badgeLabel={stackingLabel}
        />
      ))}
      {currentLabelsToStack.length > 3 ? (
        <>
          <span className="text-xs">
            +{currentLabelsToStack.length - 3} label
            {currentLabelsToStack.length - 3 > 1 ? "s" : ""}
          </span>
        </>
      ) : (
        <></>
      )}
    </>
  );

  return (
    <>
      <style>{scrollbarStyles}</style>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
          <div>
            <Badge
              variant="outline"
              className={cn(
                "h-6 py-0 px-2 text-xs font-normal flex items-center gap-1.5 cursor-pointer",
                "hover:bg-secondary-n-800 bg-secondary-n-950",
                className
              )}
            >
              {currentLabelsToStack.length > 0 ? (
                <StackedBadgeContent />
              ) : (
                <BadgeContent showName={true} badgeLabel={label} />
              )}
            </Badge>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          ref={menuRef}
          className="w-[200px] overflow-hidden bg-secondary-0 rounded-lg"
          onClick={(e) => e.stopPropagation()}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            setLabelView("list");
            setNewLabelName("");
            setSearchQuery("");
          }}
          onPointerDownOutside={(e) => {
            if (!menuRef.current?.contains(e.target as Node)) {
              setIsOpen(false);
            }
          }}
          onInteractOutside={(e) => {
            if (searchInputRef.current?.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
        >
          {labelView === "list" && (
            <>
              <div className="px-2 py-1.5">
                <Input
                  ref={searchInputRef}
                  placeholder="Change label"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  onKeyUp={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    e.preventDefault();
                    searchInputRef.current?.focus();
                  }}
                  className="h-7 text-sm bg-secondary-n-950 border-0"
                />
              </div>
              <DropdownMenuSeparator />
              <div
                className="max-h-[200px] overflow-y-auto overflow-x-hidden context-menu-scroll"
                style={{
                  scrollbarWidth: "thin",
                  scrollbarColor: "rgba(255, 255, 255, 0.3) transparent",
                }}
              >
                {sortedAndFilteredLabels.applied.length > 0 && (
                  <>
                    {sortedAndFilteredLabels.applied.map((l) => (
                      <DropdownMenuItem
                        key={l.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLabelToggle(l.id);
                        }}
                        className="flex items-center hover:bg-secondary-n-800"
                      >
                        <div className="flex items-center w-full py-1.5 px-2 min-w-0">
                          <div className="w-4 flex items-center mr-1 gap-1 flex-shrink-0">
                            <div
                              className="size-5 rounded-sm flex items-center justify-center bg-secondary-n-700 border border-secondary-n-700"
                              style={{ padding: "2px" }}
                            >
                              <CheckIcon className="size-3.5" color="#F5F5F6" />
                            </div>
                          </div>
                          <div className="flex items-center gap-3 ml-3 min-w-0">
                            <div
                              className="size-4 rounded-[3px] flex-shrink-0"
                              style={{
                                backgroundColor: l.color?.value || l.color,
                              }}
                            />
                            <span className="truncate">
                              {l.label || l.name}
                            </span>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                {sortedAndFilteredLabels.available.length > 0 && (
                  <>
                    {sortedAndFilteredLabels.applied.length > 0 && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="caption text-2xs font-medium text-secondary-n-400 ml-2">
                          Labels
                        </DropdownMenuLabel>
                      </>
                    )}
                    {sortedAndFilteredLabels.available.map((l) => (
                      <DropdownMenuItem
                        key={l.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleLabelToggle(l.id);
                        }}
                        className="flex items-center hover:bg-secondary-n-800"
                      >
                        <div className="flex items-center w-full py-1.5 px-2 min-w-0">
                          <div className="w-4 flex items-center mr-1 gap-1 flex-shrink-0">
                            <div
                              className="size-5 rounded-sm flex items-center justify-center bg-transparent border border-secondary-n-700"
                              style={{ padding: "2px" }}
                            >
                              <div className="size-3.5" />
                            </div>
                          </div>
                          <div className="flex items-center gap-3 ml-3 min-w-0">
                            <div
                              className="size-4 rounded-[3px] flex-shrink-0"
                              style={{
                                backgroundColor: l.color?.value || l.color,
                              }}
                            />
                            <span className="truncate">
                              {l.label || l.name}
                            </span>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}

                {sortedAndFilteredLabels.applied.length === 0 &&
                  sortedAndFilteredLabels.available.length === 0 && (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No labels found
                    </div>
                  )}
              </div>
              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLabelView("new");
                  setSearchQuery("");
                }}
                className="flex items-center justify-center gap-2 text-sm cursor-pointer bg-background-CTA hover:!brightness-110 text-white m-1 mt-2 rounded-md"
              >
                <Plus className="size-4" />
                Create new label
              </DropdownMenuItem>
            </>
          )}

          {labelView === "new" && (
            <>
              <div className="px-2 py-1.5">
                <Input
                  ref={labelInputRef}
                  placeholder="Label name..."
                  value={newLabelName}
                  onChange={(e) => setNewLabelName(e.target.value)}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    if (e.key === "Enter" && newLabelName.trim()) {
                      e.preventDefault();
                      setLabelView("colorSelect");
                    }
                  }}
                  onKeyUp={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onBlur={(e) => {
                    e.preventDefault();
                    labelInputRef.current?.focus();
                  }}
                  className="h-7 text-sm bg-secondary-n-950 border-0"
                />
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={!newLabelName?.trim()}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLabelView("colorSelect");
                }}
                className="flex justify-start gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
              >
                <Plus className="size-4" />
                <span className="overflow-hidden break-words">
                  Add new label: {newLabelName}
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex justify-between gap-2 cursor-pointer hover:bg-accent hover:text-accent-foreground"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setLabelView("list");
                  setNewLabelName("");
                }}
              >
                Back
                <ChevronLeft className="size-4" />
              </DropdownMenuItem>
            </>
          )}

          {labelView === "colorSelect" && (
            <>
              <DropdownMenuItem
                disabled
                className="overflow-hidden break-words"
              >
                Pick a color for label: {newLabelName}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {COLOR_LIST.map((color) => (
                <DropdownMenuItem
                  key={color.key}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleColorSelect(color);
                  }}
                  className="flex gap-2 items-center text-sm cursor-pointer"
                >
                  <div
                    className="size-2.5 rounded-[3px] flex-shrink-0"
                    style={{ backgroundColor: color.value }}
                  />
                  <span>{color.label}</span>
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  );
};
